import {
    ColDef,
    ExcelCell,
    ExcelDataType,
    ExcelExportParams,
    ExcelRow,
    ProcessCellForExportParams,
    ProcessHeaderForExportParams,
    ShouldRowBeSkippedParams,
} from 'ag-grid-community';
import { getDisplayText, hasProperty } from '../functions/helpers';
import { get, isNull } from 'lodash';
import { ROExcelExportParams } from '@models/export';
import { RowNode } from 'ag-grid-community';

const EXCEL_MAX_SHEET_NAME_LENGTH = 31;
const MIN_COL_WIDTH = 50;

// This function is not covered by tests
export function exportToExcel(excelParams?: ROExcelExportParams) {
    if (!excelParams) {
        return;
    }
    excelParams.excludedColumns = excelParams?.excludedColumns ?? [];
    excelParams.skipRowGroups = excelParams?.skipRowGroups ?? true;
    excelParams.exportActiveRowGroups = excelParams?.exportActiveRowGroups ?? true;

    const exportParams: ExcelExportParams = {};
    const columnSource = excelParams.exportHiddenColumns
        ? excelParams.gridApi.getAllGridColumns()
        : excelParams.gridApi.getAllDisplayedColumns();
    exportParams.columnKeys = columnSource
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        ?.filter((col) => {
            const parent = col.getParent()?.isExpanded();
            const colGroupShow = col.getColDef().columnGroupShow;
            return parent === null || !colGroupShow || (parent && colGroupShow === 'open') || (!parent && colGroupShow === 'closed');
        })
        .filter((col) => {
            const isPcodeColumn = col.getColId().includes('pcodeValue');
            const nonTieredColumn = col.getColId() === col.getColDef().field;
            if (isPcodeColumn && nonTieredColumn) {
                col.getColDef().hide = false;
            }
            return (
                (!col.getColDef().hide ||
                    excelParams.exportHiddenColumns ||
                    (col.isRowGroupActive() && excelParams.exportActiveRowGroups)) &&
                !excelParams.excludedColumns?.includes(col.getColDef().field)
            );
        })
        .map((col) => col.getColId());

    exportParams.columnKeys = exportParams.columnKeys ?? [];

    const firstVisibleColumn = excelParams?.gridApi
        .getColumns()
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ?.find((col) => !col.getColDef().hide && !excelParams?.excludedColumns?.includes(col.getColDef().field));

    if (excelParams?.ratecardExport) {
        const columnsToExport: string[] = [];

        excelParams.gridApi.getColumnDefs()?.forEach((colGroupParent) => {
            if ('children' in colGroupParent) {
                colGroupParent.children.forEach((colGroupChild) => {
                    if ('children' in colGroupChild) {
                        colGroupChild.children.forEach((col: ColDef) => {
                            columnsToExport.push(col.colId);
                        });
                    }
                });
            }
        });

        exportParams.columnKeys = columnsToExport;
    }

    exportParams.skipRowGroups = excelParams?.skipRowGroups;

    exportParams.shouldRowBeSkipped = (params: ShouldRowBeSkippedParams) => {
        if (!excelParams?.exportDisplayedOnly) {
            return false;
        }
        return !params.node.displayed;
    };
    exportParams.autoConvertFormulas = true;

    exportParams.processCellCallback = (params: ProcessCellForExportParams) => {
        const { node, column, value } = params;
        const { field, headerComponentParams } = column.getColDef();
        const data =
            (node as RowNode).data || (excelParams?.getDataForRowGroups ? excelParams?.getDataForRowGroups(node as RowNode) : null);

        const dataValue = data && field ? get(data, field) : null;

        if (['currency', 'number'].includes(headerComponentParams?.format?.type) && isNull(value)) {
            return;
        }

        if (!node?.data && !excelParams?.getDataForRowGroups) {
            return;
        }

        if (excelParams?.cellFilter instanceof Function && !excelParams.cellFilter(params)) {
            return;
        }

        if (dataValue?.value?.current === null) {
            return null;
            // TODO: there *has* to be a better way in how metrics are sent from the API
            // or parsed here - they shouldn't be nested at all sorts of different levels
            // Ultimately this whole callback should be refactored
        }

        const isPercent =
            (data?.format?.type === 'percent' || headerComponentParams?.format?.type === 'percent') &&
            field &&
            !field.startsWith('segmentation');
        if (isPercent && value !== null && !isNaN(Number(value))) {
            return value / 100;
        }

        // Removing formatters for numbers/currency/percents - these change type to text instead of numbers in excel
        // For formatting that flows from the app, if it doesn't exist it must be added to ag-grid-builder.ts
        // There these types are defined in the styling instead of forcibly string formatted
        if (hasProperty(dataValue, 'value')) {
            if (hasProperty(dataValue?.value, 'current')) {
                return dataValue.value.current;
            }
        }

        if (excelParams?.hasTotalRow && data?.isTotalRow && column === firstVisibleColumn) {
            return 'Total:';
        }

        if (hasProperty(value, 'name')) {
            return value.name;
        } else if (hasProperty(dataValue, 'name')) {
            return dataValue?.name;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return ''; // Handle empty array case
            }
            if (typeof value[0] === 'object' && 'url' in value[0]) {
                const latestUrl = value.reduce((latest, current) => {
                    // If the current object doesn't have a timestamp, skip it
                    if (current == null) {
                        return latest;
                    }
                    // If the latest object doesn't have a timestamp, replace it with the current one
                    if (latest == null) {
                        return current;
                    }

                    // Compare dates and return the object with the most recent timestamp
                    return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
                }, data[0] || null);
                const url = latestUrl.url;
                const displayText = latestUrl.linkText || 'Click Here';
                // return {
                //     value: `HYPERLINK("${url}", "${displayText}")`,
                //     type: 'Formula'  // Ensure this is treated as a formula in Excel
                // };
                return `=HYPERLINK("${url}", "${displayText}")`;
            }
            if (getDisplayText(value[0]?.name)) {
                return value.map((item) => getDisplayText(item)).join(', ');
            }
            return value.join(', ');
        }

        if (typeof value === 'string') {
            // prevent csv injection
            if (['=', '-', '"', '@', '+', 0x09, 0x0d].includes(value[0])) {
                return `'${value}`;
            }
            return value;
        }

        if (dataValue?.value === null) {
            return null;
        }

        return dataValue?.value || dataValue;
    };
    exportParams.processHeaderCallback = (params: ProcessHeaderForExportParams) => params.column.getColDef()?.headerComponentParams?.name;

    exportParams.appendContent = [];
    if (excelParams?.footerData) {
        exportParams.appendContent = generateCustomCells(excelParams.footerData, exportParams.columnKeys.length, 'white-background').filter(
            (row) => row,
        );
        exportParams.appendContent.unshift({
            cells: [
                {
                    data: {
                        value: '',
                        type: 'String' as ExcelDataType,
                    },
                },
                {
                    data: {
                        value: '',
                        type: 'String' as ExcelDataType,
                    },
                },
            ],
        });
    }
    const emptyHeaderCell: ExcelCell = {
        data: {
            type: 'Boolean',
            value: null,
        },
        styleId: 'no-line-header',
    };
    exportParams.prependContent = [];
    if (excelParams?.logoData) {
        const rateOpticsLogo = excelParams.logoData[0];
        const customerLogo = excelParams.logoData[1];

        const prependRows: ExcelRow = { cells: [] };
        prependRows.cells.push({
            data: {
                type: 'String',
                // value only accepts a string, but image works, and we need an image here
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value: customerLogo?.image,
                mergeAcross: 1,
            },
            styleId: 'no-line-header',
        });
        prependRows.cells.push(emptyHeaderCell);
        fillTableWithColor(prependRows, exportParams.columnKeys.length, 'no-line-header');
        exportParams.prependContent.push(prependRows);

        const appendRows: ExcelRow = { cells: [] };
        fillTableWithColor(appendRows, exportParams.columnKeys.length, 'no-line-header');
        appendRows.cells.pop();
        appendRows.cells.pop();
        appendRows.cells.pop();
        appendRows.cells.push({
            data: {
                type: 'String',
                value: 'Powered By: ',
            },
            styleId: 'no-line-header',
        });
        appendRows.cells.push({
            data: {
                type: 'String',
                // value only accepts a string, but image works, and we need an image here
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value: rateOpticsLogo.image,
                mergeAcross: 1,
            },
            styleId: 'no-line-header',
        });
        appendRows.cells.push(emptyHeaderCell);
        exportParams.appendContent.push(appendRows);
    }

    if (excelParams?.headerData) {
        exportParams.prependContent = exportParams.prependContent.concat(
            generateCustomCells(excelParams.headerData, exportParams.columnKeys.length, 'gray-background').filter((row) => row),
        );
    }

    if (excelParams?.wrappingHeader) {
        exportParams.prependContent.push({
            cells: [
                {
                    data: { value: excelParams.wrappingHeader, type: 'String' },
                    styleId: 'header',
                    mergeAcross: exportParams.columnKeys.length - 1,
                },
            ],
        });
    }
    const imageRows = [1];
    if (excelParams?.logoData) {
        const prependedRows = exportParams.prependContent.length;
        const appendedRows = exportParams.appendContent.length;
        const gridRows = excelParams.gridApi.getModel()?.getRowCount();
        const lastRowIndex = prependedRows + appendedRows + (excelParams.numberOfHeaders ?? 0) + gridRows;
        imageRows.push(lastRowIndex);
        exportParams.addImageToCell = (rowIndex, col, value) => {
            if (![1, lastRowIndex].includes(rowIndex) || !value || [' ', 'Powered By: '].includes(value)) {
                return;
            }

            const colIdx = exportParams.columnKeys?.indexOf(col?.getColId());
            return {
                image: {
                    id: 'logo' + colIdx,
                    base64: value,
                    imageType: 'png',
                    width: 200,
                    height: 50,
                    position: {
                        colSpan: 2,
                        offsetY: 5,
                        column: colIdx + 10,
                    },
                    recolor: 'Washout',
                },
            };
        };
    }
    exportParams.rowHeight = (params) => {
        if (excelParams.logoData && imageRows.includes(params.rowIndex)) {
            return params.rowIndex === imageRows[1] ? 24 : 36;
        }
        return 15;
    };
    exportParams.skipColumnGroupHeaders = false;
    exportParams.columnWidth = (params) => Math.max(params.column?.getActualWidth() * 1.5, MIN_COL_WIDTH);
    exportParams.fileName = excelParams.fileName.replace(/[^A-Za-z\d -]/g, ' ').trim();
    exportParams.sheetName = exportParams.fileName.slice(0, EXCEL_MAX_SHEET_NAME_LENGTH);
    if (excelParams.getAsSheet) {
        return excelParams.gridApi.getSheetDataForExcel(exportParams);
    }
    excelParams.gridApi.exportDataAsExcel(exportParams);
}

function isArray(temp: unknown): temp is Array<unknown> {
    return Array.isArray(temp);
}

export function generateCustomCells(
    data: { [key: string]: { data?: unknown; direction?: 'down' | 'across'; singleCell?: boolean } },
    lineLength: number,
    color: string,
): ExcelRow[] {
    const customCells: ExcelRow[] = [];

    Object.entries(data).forEach(([key, value]) => {
        if (isArray(value.data) && value.data.length > 0) {
            const row: ExcelRow = { cells: [] };
            row.cells.push({ data: { value: key, type: 'String' as ExcelDataType }, styleId: color + '-key' });

            if (value.direction === 'across' && value.singleCell) {
                row.cells.push({ data: { value: value.data.join(', '), type: 'String' }, styleId: color });
                fillTableWithColor(row, lineLength, color);
                customCells.push(row);
            } else if (value.direction === 'across') {
                const temp = data[key].data;
                if (isArray(temp)) {
                    row.cells.push(
                        ...temp.map((line) => ({ data: { value: getDisplayText(line), type: 'String' as ExcelDataType, styleId: color } })),
                    );
                    fillTableWithColor(row, lineLength, color);
                    customCells.push(row);
                }
            } else {
                row.cells.push({
                    data: { value: getDisplayText(value.data[0]), type: 'String' as ExcelDataType },
                    styleId: color,
                });
                fillTableWithColor(row, lineLength, color);
                customCells.push(row);

                value.data.slice(1).forEach((line) => {
                    const nextRow: ExcelRow = { cells: [] };
                    nextRow.cells.push({ data: { value: '', type: 'String' as ExcelDataType }, styleId: color + '-key' });
                    nextRow.cells.push({
                        data: { value: getDisplayText(line), type: 'String' as ExcelDataType },
                        styleId: color,
                    });
                    fillTableWithColor(nextRow, lineLength, color);
                    customCells.push(nextRow);
                });
            }
        } else if (getDisplayText(value.data) || getDisplayText(value.data) === '') {
            const displayText = getDisplayText(value.data ?? '');
            const row: ExcelRow = {
                cells: [
                    {
                        data: { value: key, type: 'String' as ExcelDataType },
                        styleId: color + '-key',
                    },
                ],
            };
            if (displayText.trim()) {
                row.cells.push({
                    data: { value: displayText, type: 'String' as ExcelDataType },
                    styleId: color,
                });
            }
            fillTableWithColor(row, lineLength, color);
            customCells.push(row);
        }
    });
    return customCells;
}

function fillTableWithColor(row: ExcelRow, lineLength: number, color: string): ExcelRow {
    while (row.cells.length < lineLength) {
        row.cells.push({ data: { value: null, type: 'Boolean' }, styleId: color });
    }
    return row;
}
